// Template Configuration Index
// Exports all injury type templates

import kneeTemplate from './knee.config.js';
import shoulderTemplate from './shoulder.config.js';
import ankleTemplate from './ankle.config.js';
import lumbarTemplate from './lumbar.config.js';
import achillesTemplate from './achilles.config.js';
import cervicalTemplate from './cervical.config.js';
import thoracicTemplate from './thoracic.config.js';
import concussionTemplate from './concussion.config.js';
import elbowTemplate from './elbow.config.js';
import footTemplate from './foot.config.js';
import hamstringTemplate from './hamstring.config.js';
import hipTemplate from './hip.config.js';
import wristTemplate from './wrist.config.js';
import handTemplate from './hand.config.js';
import ribTemplate from './rib.config.js';
import pelvisTemplate from './pelvis.config.js';
import thighTemplate from './thigh.config.js';
import tibiaTemplate from './tibia.config.js';

// Template registry
export const templates = {
  KNEE: kneeTemplate,
  SHOULDER: shoulderTemplate,
  ANKLE: ankleTemplate,
  LUMBAR_SPINE: lumbarTemplate,
  THORACIC_SPINE: thoracicTemplate,
  HIP: hipTemplate,
  FOOT: footTemplate,
  ACHILLES: achillesTemplate,
  HAMSTRING: hamstringTemplate,
  ELBOW: elbowTemplate,
  WRIST: wristTemplate,
  HAND: handTemplate,
  RIB: ribTemplate,
  PELVIS: pelvisTemplate,
  THIGH: thighTemplate,
  TIBIA: tibiaTemplate,
  CERVICAL_SPINE: cervicalTemplate,
  CONCUSSION: concussionTemplate
};

// Get template by ID (case-insensitive with keyword fallback)
export const getTemplate = (injuryType) => {
  if (!injuryType) return templates.KNEE;

  // Exact match first
  const template = templates[injuryType];
  if (template) return template;

  // Case-insensitive match (e.g., "Knee" -> "KNEE", "lumbar_spine" -> "LUMBAR_SPINE")
  const upperType = injuryType.toUpperCase().replace(/\s+/g, '_');
  const caseMatch = templates[upperType];
  if (caseMatch) return caseMatch;

  // Keyword-based detection (e.g., "Left Knee Injury" -> KNEE template)
  const detected = detectInjuryType(injuryType);
  if (detected && templates[detected]) {
    console.info(`Template matched "${injuryType}" to ${detected} via keyword detection`);
    return templates[detected];
  }

  console.warn(`Template not found for injury type: ${injuryType}, defaulting to KNEE`);
  return templates.KNEE;
};

// Injury types currently switched on for the rollout. Starts with knees and
// widens from there. Keep in sync with backend/config/enabledInjuryTypes.js —
// the backend rejects anything not enabled there, so adding a type here alone
// will produce a rejected submission rather than a working one.
// Set to null to offer every template.
export const ENABLED_INJURY_TYPES = ['KNEE'];

const isEnabled = (key) =>
  ENABLED_INJURY_TYPES === null || ENABLED_INJURY_TYPES.includes(key);

// Get all available templates (filtered to the enabled rollout)
export const getAvailableTemplates = () => {
  return Object.keys(templates)
    .filter(isEnabled)
    .map(key => ({
      id: key,
      name: templates[key].name,
      icon: templates[key].icon
    }));
};

// Every template regardless of rollout state — for rendering a patient record
// whose injury type was captured before that type was switched off.
export const getAllTemplatesUnfiltered = () => {
  return Object.keys(templates).map(key => ({
    id: key,
    name: templates[key].name,
    icon: templates[key].icon
  }));
};

// Check if template exists
export const hasTemplate = (injuryType) => {
  return injuryType in templates;
};

// Template detection keywords for AI
export const templateKeywords = {
  KNEE: ['knee', 'acl', 'mcl', 'meniscus', 'patella', 'pcl'],
  SHOULDER: ['shoulder', 'rotator cuff', 'subacromial', 'glenohumeral', 'clavicle', 'ac joint', 'acromioclavicular'],
  ANKLE: ['ankle', 'lateral ligament', 'atfl', 'cfl', 'syndesmosis', 'malleolus', 'talus'],
  LUMBAR_SPINE: ['lumbar', 'lower back', 'low back', 'l4', 'l5', 's1', 'sciatica', 'radiculopathy', 'disc', 'cauda equina'],
  THORACIC_SPINE: ['thoracic', 'mid back', 'mid-back', 'middle back', 't-spine', 't4', 't5', 't6', 't7', 't8', 't12', 'scheuermann', 'kyphosis'],
  HIP: ['hip', 'labral', 'fai', 'femoroacetabular', 'trochanter', 'gluteal'],
  FOOT: ['foot', 'plantar', 'metatarsal', 'heel', 'morton', 'bunion', 'midfoot'],
  ACHILLES: ['achilles', 'heel cord', 'calcaneal tendon', 'achilles tendon'],
  HAMSTRING: ['hamstring', 'posterior thigh', 'biceps femoris', 'semimembranosus', 'semitendinosus'],
  ELBOW: ['elbow', 'tennis elbow', 'golfer elbow', 'lateral epicondyl', 'medial epicondyl', 'olecranon'],
  WRIST: ['wrist', 'carpal', 'scaphoid', 'tfcc', 'de quervain'],
  HAND: ['hand', 'finger', 'fingers', 'thumb', 'metacarpal', 'phalanx', 'phalanges', 'mallet finger', 'jersey finger', 'boutonniere', "skier's thumb", 'gamekeeper', 'ucl thumb', "boxer's fracture", 'pip', 'dip', 'mcp'],
  RIB: ['rib', 'ribs', 'chest wall', 'costochondritis', 'rib fracture', 'rib stress', 'intercostal', 'sternum', 'sternal', 'thoracic cage'],
  PELVIS: ['pelvis', 'pelvic', 'groin', 'osteitis pubis', 'pubic', 'symphysis', 'adductor', 'sacroiliac', 'si joint', 'sij', 'tailbone', 'coccyx', 'coccydynia'],
  THIGH: ['thigh', 'quadriceps', 'quad', 'rectus femoris', 'vastus', 'corked thigh', 'haematoma', 'hematoma', 'contusion', 'myositis ossificans', 'dead leg'],
  TIBIA: ['tibia', 'tibial', 'shin', 'shin splints', 'mtss', 'medial tibial stress', 'tibial stress', 'compartment syndrome', 'tibial shaft', 'calf', 'calf strain', 'calf tear', 'gastrocnemius', 'soleus', 'lower leg'],
  CERVICAL_SPINE: ['cervical', 'neck', 'c-spine', 'c5', 'c6', 'c7', 'whiplash'],
  CONCUSSION: ['concussion', 'head injury', 'tbi', 'brain injury', 'head trauma', 'mTBI']
};

// Detect injury type from text
export const detectInjuryType = (text) => {
  const lowerText = text.toLowerCase();
  
  // Check each template's keywords
  for (const [type, keywords] of Object.entries(templateKeywords)) {
    for (const keyword of keywords) {
      if (lowerText.includes(keyword)) {
        return type;
      }
    }
  }
  
  return null; // No match found
};

export default templates;
